/**
 * One line per operator, not one line per radio.
 *
 * Missions are assigned by entity, and an entity expands to everything it
 * carries — a phone, a watch, a vehicle beacon, a wardriver. That is the right
 * unit to *monitor*, because each of those is a separate thing that can go
 * quiet, and the wrong unit to *read*, because they are all in the same vehicle
 * and four rows repeating one name is four times the height for one position.
 *
 * So the panel collapses to the entity and keeps the devices a click away. The
 * group still has to be honest about the fleet underneath it, which is what
 * `lead` and `quiet` are for: the summary speaks with the voice of a device
 * that is actually reporting, and says how many are not.
 */
import { MissionDeviceState } from '../../services/missionApi';

export interface DeviceGroup {
  /** Stable key: the entity, or the device itself when it has no entity. */
  key: string;
  /**
   * The operator name, or the device's own label for one assigned directly.
   * Empty when neither is recorded, which the caller resolves from the device
   * id — it holds the roster's naming, and this module should not guess.
   */
  label: string;
  /** True when this is a real grouping and not a lone device standing in. */
  grouped: boolean;
  devices: MissionDeviceState[];
  /** The device whose position and state speak for the group. */
  lead: MissionDeviceState;
  /** How many of the group are reporting no position at all. */
  quiet: number;
  /** Oldest last-fix among the silent devices, for "quiet 12m". */
  quietSince?: string | null;
  /** Compact age of the oldest quiet device, e.g. "12m". */
  quietFor?: string | null;
}

/**
 * Worse states win, so collapsing can never hide a problem behind a sibling
 * that happens to be fine.
 */
function severity(device: MissionDeviceState): number {
  if (device.route_state === 'off_route') return 3;
  if (device.signal_state === 'lost') return 2;
  if (device.route_state === 'on_route') return 1;
  return 0;
}

function silent(device: MissionDeviceState): boolean {
  return device.signal_state === 'lost';
}

function quietAge(iso?: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

/**
 * The device that speaks for the group.
 *
 * Preference goes to one that is actually reporting: every device on an entity
 * is in the same vehicle, so any live fix is the operator's position, and a
 * summary reading "no position yet" while the phone is reporting would be
 * wrong. Among those, the worst state wins.
 */
function pickLead(devices: MissionDeviceState[]): MissionDeviceState {
  const speaking = devices.filter((d) => !silent(d));
  const pool = speaking.length ? speaking : devices;
  return pool.reduce((best, d) => (severity(d) > severity(best) ? d : best), pool[0]);
}

/**
 * Devices in the order given, collapsed onto their operator.
 *
 * Devices assigned directly rather than through an entity carry no `entity_id`
 * and each stand alone, which is honest: the roster does not say they belong
 * together, so the panel must not imply it.
 */
export function groupDevices(devices: MissionDeviceState[]): DeviceGroup[] {
  const order: string[] = [];
  const buckets = new Map<string, MissionDeviceState[]>();

  devices.forEach((device) => {
    const entity = (device.entity_id || '').trim();
    const key = entity || `device:${device.device_id}`;
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(device);
  });

  return order.map((key) => {
    const members = buckets.get(key)!;
    const entity = (members[0].entity_id || '').trim();
    return {
      key,
      label: entity || members[0].label || '',
      grouped: !!entity,
      devices: members,
      lead: pickLead(members),
      quiet: members.filter(silent).length,
      quietSince: members.filter(silent)
        .map((d) => d.last_fix_at)
        .filter((at): at is string => !!at)
        .sort()[0] || null,
      quietFor: quietAge(
        members.filter(silent)
          .map((d) => d.last_fix_at)
          .filter((at): at is string => !!at)
          .sort()[0] || null,
      ),
    };
  });
}

/**
 * What to call a device once its operator is already named above it.
 *
 * Falls back to splitting the stored label, which is written as
 * "<entity> · <kind>", for rows saved before the kind was recorded.
 */
export function deviceKindLabel(device: MissionDeviceState): string {
  if (device.device_kind) return device.device_kind;
  const label = device.label || '';
  const sep = label.lastIndexOf(' · ');
  if (sep >= 0) return label.slice(sep + 3);
  return label || device.device_id;
}
